import React, { useState } from 'react';
import { BentoCard } from '../BentoCard';
import { TimeBin } from './charts/KalenderwocheBarChart';
import { QuartalBalken } from './charts/QuartalBalken';
import { TagVerlauf } from './charts/TagVerlauf';
import { TagBalken } from './charts/TagBalken';
import { KwVerlauf } from './charts/KwVerlauf';
import { KwBalken } from './charts/KwBalken';
import { QuartalVerlauf } from './charts/QuartalVerlauf';
import { theme } from '../../theme';
import type { PeriodResidueBin, PrimaryAxisVolumeBin } from '../../serverTypes';

// <purpose>TicketaufkommenCard — SIG 2 Ticketaufkommen card: 6-variant ECharts (Tag/KW/Q × Verlauf/Balken).</purpose>
// <dependencies>dailyVolume, kwVolume TimeBin[], qVolume TimeBin[], rawCategoryData PrimaryAxisVolumeBin[], BentoCard, ECharts sub-components</dependencies>
// <constraints>
//   Sub-toggle (Verlauf/Balken) is card-local state — does not lift to StatistikSeite.
//   Quartal: Verlauf = QuartalVerlauf sunburst; Balken = QuartalBalken horizontal Gantt strip.
//   Aggregation toggle lives in A0 global filter row — no in-card control.
//   part 7 round 5, 2026-10-14 (operator-accepted screen): the three Balken charts plot the classified volume only;
//   a bin's residue (residueBins = stats.perPeriodResidue) is its label's second small line „+f +w". Of the Verlauf
//   variants only KW.Verlauf prints per-bin totals, so it speaks the same language; Tag.Verlauf (no labels) and the
//   Quartal sunburst stay as they are. Tag.Balken reads the daily bins for its classified total.
//   rawCategoryData is optional; KW.Balken + QuartalBalken hover-morph + QuartalVerlauf per-K breakdown degrade gracefully if absent.
// </constraints>

export type Aggregation = 'daily' | 'kw' | 'q';
export type SubMode = 'verlauf' | 'balken';

// Re-export TimeBin so StatistikSeite.tsx import from this module still resolves.
// (StatistikSeite imports 'type { TimeBin } from ./charts/KalenderwocheBarChart' directly — preserved.)

const SUB_PILL_STYLE = (active: boolean): React.CSSProperties => ({
  padding: '3px 9px',
  borderRadius: 10,
  border: `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
  cursor: 'pointer',
  background: active ? theme.colors.navy : 'white',
  // WCAG 1.4.3 — was '#777' (4.47:1 on white, just below AA at 10px); textSecondary = 5.97:1
  color: active ? 'white' : theme.colors.textSecondary,
  fontSize: 10,
  fontWeight: active ? 600 : 400,
  transition: 'all 0.18s',
});

type Props = {
  dailyVolume: Array<{ date: string; count: number; binLabel?: string }>;
  kwVolume: TimeBin[];
  qVolume: TimeBin[];
  aggregation: Aggregation;
  // Which aggregation produced the data currently in {daily,kw,q}Volume. When this
  // differs from `aggregation`, the user just switched tabs and a refetch is in flight
  // — render a loading placeholder instead of feeding stale data into the new chart.
  statsAggregation: Aggregation | null;
  onAggregationChange: (a: Aggregation) => void;
  filterHash: string;
  // Per-K breakdown for KW.Balken hover-morph — optional, comes from stats.perPrimaryAxisVolumeByPeriod
  rawCategoryData?: PrimaryAxisVolumeBin[];
  // stats.perPeriodResidue — optional; absent ⇒ no „+n" on any bar.
  residueBins?: PeriodResidueBin[];
};

// Why: aggregation toggle promoted to A0 global filter row — no in-card control needed
export const TicketaufkommenCard: React.FC<Props> = ({
  dailyVolume, kwVolume, qVolume, aggregation, statsAggregation,
  onAggregationChange: _onAggregationChange,
  filterHash, rawCategoryData, residueBins,
}) => {
  // Guard: only render the active-tab chart when its data has actually been fetched
  // for this aggregation. Otherwise show a loading placeholder.
  const dataMatchesTab = statsAggregation === aggregation;
  const loadingPlaceholder = (
    <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
      Lädt…
    </div>
  );
  // Default to Balken per operator preference (visual review 2026-05-22).
  const [subMode, setSubMode] = useState<SubMode>('balken');

  return (
    <BentoCard
      title="Ticketaufkommen"
      headerAction={
        // Sub-toggle right-aligned in card header — shown for all three aggregation modes
        aggregation !== null ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {/* WCAG 1.4.3 — was '#888' (3.54:1 on white, fail AA); textSecondary = 5.97:1 */}
            <span style={{ fontSize: 10, color: theme.colors.textSecondary, marginRight: 4 }}>Darstellung:</span>
            <span
              style={SUB_PILL_STYLE(subMode === 'verlauf')}
              onClick={() => setSubMode('verlauf')}
              role="button"
              aria-pressed={subMode === 'verlauf'}
            >
              Verlauf
            </span>
            <span
              style={SUB_PILL_STYLE(subMode === 'balken')}
              onClick={() => setSubMode('balken')}
              role="button"
              aria-pressed={subMode === 'balken'}
            >
              Balken
            </span>
          </span>
        ) : undefined
      }
    >
      {aggregation === 'daily' && (
        !dataMatchesTab ? loadingPlaceholder :
        dailyVolume.length > 0 ? (
          subMode === 'verlauf' ? (
            <TagVerlauf data={dailyVolume} filterHash={filterHash} />
          ) : (
            <TagBalken data={dailyVolume} rawData={rawCategoryData} residue={residueBins} filterHash={filterHash} />
          )
        ) : (
          <div style={{ minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
            Keine Volumendaten
          </div>
        )
      )}
      {aggregation === 'kw' && (
        !dataMatchesTab ? loadingPlaceholder :
        kwVolume.length > 0 ? (
          subMode === 'verlauf' ? (
            <KwVerlauf data={kwVolume} rawData={rawCategoryData} residue={residueBins} filterHash={filterHash} />
          ) : (
            <KwBalken data={kwVolume} rawData={rawCategoryData} residue={residueBins} filterHash={filterHash} />
          )
        ) : (
          <div style={{ minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
            Keine Daten
          </div>
        )
      )}
      {aggregation === 'q' && (
        !dataMatchesTab ? loadingPlaceholder :
        qVolume.length > 0 ? (
          subMode === 'verlauf' ? (
            <QuartalVerlauf data={qVolume} rawCategoryData={rawCategoryData} filterHash={filterHash} />
          ) : (
            <QuartalBalken data={qVolume} rawCategoryData={rawCategoryData} residue={residueBins} filterHash={filterHash} />
          )
        ) : (
          <div style={{ minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
            Keine Daten
          </div>
        )
      )}
    </BentoCard>
  );
};
